import React, { useState } from "react";
import { Target, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { fmtPoints } from "../data/mock";
import { NOTIFY_REFRESH_EVENT } from "./NotificationBell";
import { useIsPro } from "./ProGate";

export default function TargetPointsControl({ deal, onChange }) {
  const { toast } = useToast();
  const isPro = useIsPro();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(deal.targetPoints || Math.max(1000, Math.round((deal.points * 0.9) / 500) * 500));
  const [busy, setBusy] = useState(false);

  const save = async (target) => {
    setBusy(true);
    try {
      const res = await api.setWishlistTarget(deal.savedId, target);
      onChange?.(target);
      setEditing(false);
      window.dispatchEvent(new Event(NOTIFY_REFRESH_EVENT));
      if (target == null) toast({ title: "Price watch removed" });
      else if (!isPro) toast({ title: "Target saved", description: "Notifications are a PRO feature — upgrade to start receiving price-drop alerts." });
      else if (res.newNotifications > 0) toast({ title: "Price drop found!", description: `${deal.origin.code} → ${deal.destination.code} is already under ${fmtPoints(target)} pts. Check the bell.` });
      else toast({ title: "Watching this route", description: `We’ll notify you when ${deal.origin.code} → ${deal.destination.code} drops below ${fmtPoints(target)} pts.` });
    } catch {
      toast({ title: "Could not save target", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs text-slate-600">
          <Target className={`h-3.5 w-3.5 ${deal.targetPoints ? "text-green-600" : "text-slate-400"}`} />
          {deal.targetPoints ? <span data-testid="target-points-label">Alert below <b>{fmtPoints(deal.targetPoints)}</b> pts</span> : "No price watch"}
        </span>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing(true)} data-testid="target-edit-button" className="text-xs font-semibold text-blue-600 hover:underline">{deal.targetPoints ? "Edit" : "Set target"}</button>
          {deal.targetPoints && <button onClick={() => save(null)} aria-label="Remove target" data-testid="target-remove-button" className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50/50 px-3 py-2">
      <Target className="h-3.5 w-3.5 shrink-0 text-blue-600" />
      <input type="number" step={500} min={1000} value={value} onChange={(e) => setValue(Number(e.target.value))} data-testid="target-points-input" className="h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-blue-500" />
      <span className="text-xs text-slate-500">pts</span>
      <button onClick={() => save(value)} disabled={busy || value < 1000} data-testid="target-save-button" className="flex h-8 items-center gap-1 rounded-md bg-blue-600 px-2.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Save</button>
      <button onClick={() => setEditing(false)} aria-label="Cancel" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
    </div>
  );
}
